/**
 * Provider-neutral browser-session primitives shared by every auth adapter
 * (US-008).
 *
 * Extracted verbatim from `src/auth/keycloak.ts` so the Scalekit adapter
 * inherits the exact same redirect-uri hygiene, recovery budget and
 * secure-context bounce rather than re-deriving them slightly differently. The
 * behaviours in here are the ones the provider-neutral contract
 * (`src/__tests__/authProviderContract.test.ts`) holds EVERY adapter to, so
 * there must be one implementation, not one per vendor.
 *
 * Nothing in this module imports a vendor SDK, and nothing in it stores a
 * token. The only browser storage it touches is a single sessionStorage
 * counter per provider — the recovery budget — which is what makes a broken
 * identity provider produce a broken page instead of an infinite redirect.
 */

// ───────────────────────────────────────────────────────────────────────────
// Recovery markers
//
// Every marker string any adapter can throw, in ONE place. Two of them are a
// two-sided contract with `scripts/auth-cutover-verify.py`, which greps the
// served dashboard HTML for the terminal markers; renaming one here without
// renaming it there silently blinds the deployment canary to the login loop it
// exists to catch. `src/__tests__/authProviderContract.test.ts` §5 pins both
// ends.
// ───────────────────────────────────────────────────────────────────────────

/** Keycloak adapter: the browser is navigating to the provider. */
export const KEYCLOAK_RECOVERY_REDIRECT_PENDING = 'keycloak-recovery-redirect-pending';

/** Keycloak adapter: recovery gave up on purpose. Greped by the canary. */
export const KEYCLOAK_RECOVERY_LOOP_GUARD = 'keycloak-recovery-loop-guard-tripped';

/** Scalekit adapter: the browser is navigating to the provider. */
export const SCALEKIT_RECOVERY_REDIRECT_PENDING = 'scalekit-recovery-redirect-pending';

/** Scalekit adapter: recovery gave up on purpose. Greped by the canary. */
export const SCALEKIT_RECOVERY_LOOP_GUARD = 'scalekit-recovery-loop-guard-tripped';

const REDIRECT_PENDING_MARKERS: readonly string[] = [
  KEYCLOAK_RECOVERY_REDIRECT_PENDING,
  SCALEKIT_RECOVERY_REDIRECT_PENDING,
];

const LOOP_GUARD_MARKERS: readonly string[] = [
  KEYCLOAK_RECOVERY_LOOP_GUARD,
  SCALEKIT_RECOVERY_LOOP_GUARD,
];

const messageOf = (err: unknown): string => (err instanceof Error ? err.message : '');

/**
 * True when `err` means "a full-page navigation to the identity provider is
 * already under way". The SPA must hold its loading state rather than flash an
 * unauthenticated screen through the bounce.
 */
export const isRecoveryRedirectPending = (err: unknown): boolean =>
  REDIRECT_PENDING_MARKERS.includes(messageOf(err));

/** True when `err` means "recovery gave up on purpose" — the bounded terminal state. */
export const isRecoveryTerminal = (err: unknown): boolean =>
  LOOP_GUARD_MARKERS.includes(messageOf(err));

// ───────────────────────────────────────────────────────────────────────────
// Redirect-uri construction
// ───────────────────────────────────────────────────────────────────────────

/**
 * OAuth 2.0 / OIDC callback params (RFC 6749 + OIDC Core). Stripped from the
 * captured browser URL before building the `redirect_uri` we hand to the
 * provider so stale callback params can never contaminate a fresh authorize
 * request.
 *
 * Deliberately NOT shared with sentry.ts's SENSITIVE_QUERY_PARAMS: that set
 * includes `token`, which the /accept-invite?token=… flow depends on. Mixing
 * the two lists would break invite links on recovery redirects.
 */
const OAUTH_QUERY_PARAMS = [
  'code',
  'state',
  'session_state',
  'iss',
  'error',
  'error_description',
  'error_uri',
] as const;

export const stripOAuthParams = (search: string): string => {
  if (!search) return '';
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  for (const key of OAUTH_QUERY_PARAMS) {
    params.delete(key);
  }
  const cleaned = params.toString();
  return cleaned ? `?${cleaned}` : '';
};

export const stripOAuthFragment = (hash: string): string => {
  if (!hash) return '';
  // Bare-anchor fragments (`#section`) must pass through untouched.
  // URLSearchParams('section') would parse that as a one-key empty value
  // and round-trip as `section=`, corrupting anchor-based navigation.
  if (!hash.includes('=')) return hash;
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  for (const key of OAUTH_QUERY_PARAMS) {
    params.delete(key);
  }
  const cleaned = params.toString();
  return cleaned ? `#${cleaned}` : '';
};

/**
 * The `redirect_uri` for an optional post-login path, always free of consumed
 * OAuth callback params. With no argument it reproduces the user's current
 * location (minus those params) so a deep link survives the round trip.
 */
export const buildRedirectUri = (path?: string): string => {
  if (path !== undefined) {
    return `${window.location.origin}${path}`;
  }

  const cleanedSearch = stripOAuthParams(window.location.search);
  const cleanedHash = stripOAuthFragment(window.location.hash);
  return `${window.location.origin}${window.location.pathname}${cleanedSearch}${cleanedHash}`;
};

/**
 * True when the current URL carries an authorization-server response, i.e. the
 * browser is standing on a callback rather than on an ordinary page load.
 *
 * `state` is required on both branches: it is the key the vendor SDK looks the
 * stored PKCE transaction up by, so a bare `?code=…` is not a callback we can
 * (or should) process.
 */
export const hasAuthorizationResponse = (): boolean => {
  if (typeof window === 'undefined') return false;
  const search = window.location.search;
  const hash = window.location.hash;
  const query = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const fragment = hash.includes('=')
    ? new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
    : new URLSearchParams();
  const has = (key: string): boolean => query.has(key) || fragment.has(key);
  return has('state') && (has('code') || has('error'));
};

/**
 * Rewrites the current URL via history.replaceState so any leftover OAuth
 * callback params (`?code=…`, `#state=…`, etc.) are gone BEFORE we hand
 * control back to the vendor SDK. Required because SDKs read window.location
 * internally when constructing the next transaction; leaving stale params in
 * place races the fresh state we're issuing.
 *
 * Also the precondition for Sentry Session Replay: `enableSentryReplay()` may
 * only run once the callback params are out of `window.location`.
 */
export const cleanCurrentUrl = (): void => {
  const cleaned = buildRedirectUri();
  if (cleaned !== window.location.href) {
    window.history.replaceState(null, '', cleaned);
  }
};

// ───────────────────────────────────────────────────────────────────────────
// Secure-context guard
// ───────────────────────────────────────────────────────────────────────────

// PKCE S256 needs `crypto.subtle`, which browsers expose on secure contexts
// only (https://, localhost, file://). On insecure http:// origins the vendor
// SDK throws from deep inside itself — LUTIQ-1Q/1P/1N. The dashboard is only
// served over https in prod + staging, so the only way to hit this path is a
// direct http:// visit; the right fix is to bounce the browser to https before
// any auth code runs. SSR guard keeps Vitest/jsdom happy.
const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

export const ensureSecureContext = (redirectPendingMarker: string): void => {
  if (typeof window === 'undefined') return;
  if (window.isSecureContext) return;
  if (window.location.protocol === 'https:') return;
  // Browsers treat http://localhost as a secure context (W3C Secure Contexts
  // §3.1). jsdom doesn't set `isSecureContext`, so rely on the hostname check
  // so our test suite keeps running against http://localhost without the
  // production redirect firing.
  if (LOCALHOST_HOSTNAMES.has(window.location.hostname)) return;

  const httpsUrl = `https://${window.location.host}${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.replace(httpsUrl);
  // Throw so callers short-circuit the current microtask instead of racing the
  // navigation. AuthContext.boot maps this message to the recovery-pending
  // branch, keeping the spinner on screen through the bounce.
  throw new Error(redirectPendingMarker);
};

// ───────────────────────────────────────────────────────────────────────────
// Recovery budget
// ───────────────────────────────────────────────────────────────────────────

export type RecoveryBudget = {
  /**
   * Spend one recovery attempt. Throws an `Error` carrying the adapter's
   * terminal marker once the budget is exhausted, having first dropped the
   * counter so the NEXT user-initiated attempt starts fresh — terminal means
   * "this cycle is over", not "this tab is dead".
   */
  consume(): void;
  /** Drop the counter, e.g. after a session is successfully established. */
  clear(): void;
};

/**
 * A bounded per-tab counter that prevents an infinite recover → reload loop
 * when the identity provider itself is broken. Stored in sessionStorage so a
 * reload preserves the budget within a single tab but doesn't leak across tabs.
 */
export const createRecoveryBudget = (
  storageKey: string,
  maxAttempts: number,
  terminalMarker: string,
): RecoveryBudget => ({
  consume: () => {
    let attempts = 0;
    try {
      // Clamp to 0 for any non-finite / negative value. Otherwise NaN (from a
      // bogus sessionStorage value) slips past `>= maxAttempts` because
      // `NaN >= 2` is false, letting the loop guard silently bypass.
      const raw = Number(sessionStorage.getItem(storageKey));
      attempts = Number.isFinite(raw) && raw >= 0 ? raw : 0;
    } catch {
      // sessionStorage unavailable — treat as zero and fall through.
    }
    if (attempts >= maxAttempts) {
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        // ignore
      }
      throw new Error(terminalMarker);
    }
    try {
      sessionStorage.setItem(storageKey, String(attempts + 1));
    } catch {
      // ignore
    }
  },
  clear: () => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // sessionStorage unavailable (SSR / privacy mode) — ignore.
    }
  },
});
